import { useRef } from "react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";

import { IRequestFailed, IRequestState } from "@app/types";
import { RootState } from "@app/redux/root-reducer";
import { RequestStatus, Strings } from "@app/constants";
import { useEffectExceptOnMount } from "./use-effect-except-on-mount";

// ----------------------------------------------------------------------

interface UseReturnType<T = any> extends IRequestState<T> {
  loading: boolean;
  isSuccess: boolean;
  isFailed: boolean;
}

export interface UseRequestStateProps<
  S extends (state: RootState) => IRequestState<any>
> {
  // Request state selector
  stateSelector: S;

  // Should show error snackbar
  // By default it will use api request error message
  errorShown?: boolean;

  // Should show success snackbar
  // By default it will use api request message
  successMessageShown?: boolean;

  // Use this error message instead of api message
  errorMessage?: string;

  // Use this success message instead of api message
  successMessage?: string;

  // Call on request success
  onSuccess?: (data: ReturnType<S>["data"]) => void;

  // Call on request failed
  onFailed?: (error?: IRequestFailed) => void;
}

export function useRequestState<
  S extends (state: RootState) => IRequestState<any>
>(props: UseRequestStateProps<S>): UseReturnType<ReturnType<S>["data"]> {
  const { stateSelector } = props;

  const { enqueueSnackbar } = useSnackbar();

  const { status, error, data } = useSelector(stateSelector);

  const prevStatusRef = useRef<RequestStatus | undefined>(status);
  const propsRef = useRef(props);
  propsRef.current = props;

  useEffectExceptOnMount(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = status;

    if (
      status === RequestStatus.SUCCESS &&
      prevStatus !== RequestStatus.SUCCESS &&
      propsRef.current.successMessageShown
    ) {
      enqueueSnackbar(
        `${Strings.common.success} ${
          propsRef.current.successMessage ?? (typeof data === "string" ? data : "")
        }`,
        {
          variant: "success",
        }
      );
      propsRef.current.onSuccess?.(data);
    }

    if (
      status === RequestStatus.ERROR &&
      prevStatus !== RequestStatus.ERROR &&
      propsRef.current.errorShown
    ) {
      enqueueSnackbar(
        `${Strings.common.failed} ${
          propsRef.current.errorMessage ?? error?.message ?? ""
        }`,
        {
          variant: "error",
        }
      );
      propsRef.current.onFailed?.(error);
    }
  }, [status, data, error]);

  return {
    loading: status === RequestStatus.RUNNING,
    status,
    data,
    error,
    isFailed: status === RequestStatus.ERROR,
    isSuccess: status === RequestStatus.SUCCESS,
  };
}
